import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Parser from 'html-react-parser';
import l from '../../../../lang';
import { Routes } from '../../../../config/routes';
import ButtonLink from '../../../common/button-link';
import CardTitle from '../../../common/card-title';
import Card from '../../../common/card';
import CardInfo from '../../../common/card-info';
import { segmentHomeKycStep2Complete, segmentHomeQuickLinkEditCountries } from '../../../../utils/analytics';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { Button } from '../../../common/button';
import { Tooltip } from 'react-tooltip';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/info-unfilled.svg';
import legalDocumentsUpdateIcon from '../../../../assets/images/legal-documents-update.svg?url';
import { useCompanyInfo } from '../../../../store/company-information';
import { useEffect, useState } from 'react';
import { AuthSelectors } from '../../../../store/auth/selectors';
import { canEditCompanyInformation } from '../../../../store/company-information/roles';
import { useDashboard } from '../../../../store/dashboard';

interface FirstTimeCardsProps {
  shouldAnimationsRun: boolean;
  maxAmount: number;
}

const FirstTimeCards = ({ shouldAnimationsRun, maxAmount }: FirstTimeCardsProps) => {
  const history = useHistory();
  const roles = useSelector(AuthSelectors.getRoles);
  const canEdit = canEditCompanyInformation(roles);
  const { getKycDocuments, itHasKycDocsToUpdate: itHasKycDocsToUpdateFromCompanyInfo } = useCompanyInfo();
  const { merchantMe } = useDashboard();
  const isMigration = merchantMe?.pendingKyc2Migration === true;
  const [itHasKycDocsToUpdate, setItHasKycDocsToUpdate] = useState(false);

  useEffect(() => {
    getKycDocuments();
  }, []);

  useEffect(() => {
    setItHasKycDocsToUpdate(
      itHasKycDocsToUpdateFromCompanyInfo || merchantMe?.legalTalentSessionStatus == 'awaiting_client_correction'
    );
  }, [merchantMe?.legalTalentSessionStatus, itHasKycDocsToUpdateFromCompanyInfo]);

  const onPaymentMethodsHandler = () => {
    segmentHomeQuickLinkEditCountries();
    history.push(Routes.PAYMENT_METHODS);
  };

  const onCompanyInformationHandler = () => {
    segmentHomeKycStep2Complete();
    history.push(`${Routes.COMPANY_INFORMATION}?kyc=2`);
  };

  const onReviewDocumentationHandler = () => {
    if (merchantMe?.legalTalentSessionStatus == 'awaiting_client_correction') {
      history.push(`${Routes.COMPANY_INFORMATION}?kyc=2`);
    } else {
      history.push(Routes.REVIEW_DOCUMENTATION);
    }
  };

  return (
    <Card>
      <CardTitle>
        {isMigration ? (
          <span style={{ fontSize: '22px' }}>{Parser(l('home.migration.reminder.title'))}</span>
        ) : itHasKycDocsToUpdate ? (
          <span style={{ fontSize: '22px' }}>{Parser(l('home.readyMsg.review-docs'))}</span>
        ) : (
          <span style={{ fontSize: '22px' }}>{Parser(l('home.readyMsg.firstTime'))}</span>
        )}
      </CardTitle>
      <div className={`two-cards ${shouldAnimationsRun ? 'card__animated' : ''}`}>
        <CardInfo bottomElement={<div className="card__accentuate-line" />}>
          {isMigration ? (
            <Box sx={{ textAlign: 'center' }} className="migration-deadline-card">
              <img
                src={legalDocumentsUpdateIcon}
                alt=""
                className="migration-deadline-card__icon"
                aria-hidden
              />
              <div className="card__desc migration-deadline-card__desc">
                {Parser(l('home.migration.reminder.deadline'))}
              </div>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress variant="determinate" value={itHasKycDocsToUpdate ? 90 : 75} size={100} />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span className="circular-progress__text">{itHasKycDocsToUpdate ? '90%' : '75%'}</span>
                </Box>
              </Box>
              <div className="card__desc">{Parser(l('home.complete-profile.kyc2.desc'))}</div>
            </Box>
          )}
        </CardInfo>

        <CardInfo bottomElement={<div className="card__accentuate-line" />}>
          <div
            className={
              'card__desc step2__desc' +
              (isMigration || itHasKycDocsToUpdate ? ' step2__desc--kyc' : '') +
              (isMigration ? ' step2__desc--migration' : '')
            }
          >
            <div className="step2__desc_text">
              <div>
                {isMigration ? (
                  <>
                    <h4>
                      {l('home.migration.reminder.card.title')}
                      <a id="migration-info" className="step2__desc_info">
                        <InfoIcon />
                      </a>
                    </h4>
                    <Tooltip anchorSelect="#migration-info" place="right">
                      <div className="benefits__desc">{l('home.migration.reminder.tooltip')}</div>
                    </Tooltip>
                    {Parser(l('home.migration.reminder.card.desc'))}
                  </>
                ) : itHasKycDocsToUpdate ? (
                  <>
                    <h4>{l('home.readyMsg.reviewDocs.title')}</h4>
                    {Parser(l('home.readyMsg.reviewDocs.desc'))}
                  </>
                ) : (
                  <div>
                    <ButtonLink
                      text={l('home.readyMsg.paso2.link')}
                      onClick={onCompanyInformationHandler}
                      disabled={!canEdit}
                    />
                    {Parser(l('home.readyMsg.paso2.desc'))}
                  </div>
                )}
              </div>
              {!isMigration && !itHasKycDocsToUpdate && (
                <>
                  <a id="clickable">
                    <InfoIcon />
                  </a>

                  <Tooltip anchorSelect="#clickable" clickable place="right">
                    <div className="benefits__desc">
                      <span>{l('home.readyMsg.step2.tooltip.title')}</span>
                      <ul>
                        <li>{l('home.readyMsg.step2.tooltip.item1')}</li>
                        <li>{l('home.readyMsg.step2.tooltip.item2')}</li>
                      </ul>
                      <span>
                        {l('home.readyMsg.step2.tooltip.desc1')}
                        <ButtonLink text={l('home.readyMsg.step2.tooltip.disable')} onClick={onPaymentMethodsHandler} />
                        {l('home.readyMsg.step2.tooltip.desc2')}
                      </span>
                    </div>
                  </Tooltip>
                </>
              )}
            </div>
            {isMigration ? (
              <Button textTransformNone onClick={onCompanyInformationHandler} disabled={!canEdit}>
                {l('home.migration.reminder.button')}
              </Button>
            ) : itHasKycDocsToUpdate ? (
              <Button onClick={onReviewDocumentationHandler}>{l('home.readyMsg.step2.review-docs')}</Button>
            ) : (
              <Button onClick={onCompanyInformationHandler} disabled={!canEdit}>
                {l('home.readyMsg.step2.complete-now')}
              </Button>
            )}
          </div>
        </CardInfo>
      </div>
    </Card>
  );
};

export default FirstTimeCards;
